import React from 'react';
import {
    ChevronDownIcon,
    ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { PlayCircleIcon } from '@heroicons/react/24/solid';

const LoadingSpinner = ({ size = 'md' }) => <span className={`loading loading-spinner loading-${size} text-primary`}></span>;

const DesktopPlayerTable = ({
    displayedPlayers,
    perPlayerStats,
    playerSnippets,
    allClassifications,
    expandedPlayerId,
    onToggleSnippetsExpansion,
    onPlayerNameClick, // Renamed from handlePlayerClick for clarity
    onPlaySnippet,
    selectedDays,
    selectedClassifications,
    isLoadingPlayers,
    isLoadingSnippetsAndStats,
    selectedTeamIds // Needed to determine message for empty state
}) => {

    if (isLoadingPlayers) {
        return <div className="text-center p-4"><LoadingSpinner /></div>;
    }

    if (displayedPlayers.length === 0) {
        return <p className="text-center text-base-content/70 italic py-4">{selectedTeamIds.size > 0 ? "No players for selected team(s)." : "Select a team to view players."}</p>;
    }

    return (
        <div className="overflow-x-auto hidden md:block">
            <table className="table table-sm w-full">
                <thead>
                    <tr>
                        <th>Player (Pos - Team)</th>
                        <th className="text-center" title="Total Snippets">Total</th>
                        <th className="text-center text-success" title="Positive Sentiment">Pos</th>
                        <th className="text-center text-error" title="Negative Sentiment">Neg</th>
                        {allClassifications.map(cls => (
                            <th key={cls} className="text-center whitespace-nowrap" title={cls}>
                                {cls}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {[...displayedPlayers]
                        .sort((a, b) => (perPlayerStats[b.id]?.total || 0) - (perPlayerStats[a.id]?.total || 0))
                        .map(player => {
                            const stats = perPlayerStats[player.id] || { total: 0, positive: 0, negative: 0, classifications: {} };
                            let highValueSnippets = playerSnippets[player.id]?.filter(s => s.value?.toLowerCase() !== 'low') || [];
                            if (selectedClassifications && selectedClassifications.size > 0) {
                                highValueSnippets = highValueSnippets.filter(s => selectedClassifications.has(s.classification?.trim() || "Unclassified"));
                            }

                            return (
                                <React.Fragment key={player.id}>
                                    <tr className="hover">
                                        <td>
                                            <div className="flex items-center gap-2">
                                                <button 
                                                    className="font-bold text-base-content hover:text-primary focus:outline-none text-left" 
                                                    onClick={() => onPlayerNameClick(player.id)}
                                                >
                                                    {player.name}
                                                </button>
                                                {highValueSnippets.length > 0 && (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); onToggleSnippetsExpansion(player.id); }}
                                                        className="btn btn-ghost btn-xs p-1 text-primary hover:bg-primary/10"
                                                        title={expandedPlayerId === player.id ? "Hide Snippets" : "Show High Value Snippets"}
                                                    >
                                                        {expandedPlayerId === player.id ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
                                                    </button>
                                                )}
                                            </div>
                                            <div className="text-xs opacity-70"> {player.position || 'N/A'} - {player.team || 'N/A'} </div>
                                        </td>
                                        <td className="text-center">{stats.total}</td>
                                        <td className="text-center text-success font-medium">{stats.positive}</td>
                                        <td className="text-center text-error font-medium">{stats.negative}</td>
                                        {allClassifications.map(cls => (
                                            <td key={`${player.id}-${cls}`} className="text-center">
                                                {stats.classifications[cls] || 0}
                                            </td>
                                        ))}
                                    </tr>
                                    {expandedPlayerId === player.id && (
                                        <tr className="bg-base-200/50">
                                            <td colSpan={4 + allClassifications.length} className="p-0">
                                                <div className="p-3 space-y-2">
                                                    <h4 className="font-semibold text-sm text-accent">
                                                        High Value Snippets ({highValueSnippets.length})
                                                        <span className="text-xs font-normal text-base-content/70 ml-1">
                                                            (Last {selectedDays === 0 ? 'All Time' : `${selectedDays}D`})
                                                        </span>
                                                    </h4>
                                                    {highValueSnippets.length > 0 ? (
                                                        <ul className="space-y-3 max-h-60 overflow-y-auto bg-base-100 p-3 rounded-md shadow-inner">
                                                            {highValueSnippets.map(snippet => (
                                                                <li key={snippet.id} className="text-xs p-2.5 bg-base-200 rounded-lg shadow">
                                                                    <p className="text-base-content/95 leading-relaxed whitespace-pre-wrap mb-1.5">{snippet.snippet}</p>
                                                                    <div className="mt-1 space-y-1 text-xs text-base-content/70">
                                                                        <div className="flex justify-between items-center gap-2">
                                                                            <span>
                                                                                Sentiment: <span className={`font-medium ${snippet.sentiment?.toLowerCase() === 'positive' ? 'text-success' : snippet.sentiment?.toLowerCase() === 'negative' ? 'text-error' : 'text-base-content/80'}`}>
                                                                                    {snippet.sentiment || 'N/A'}
                                                                                </span>
                                                                            </span>
                                                                            <span className="truncate" title={snippet.classification || 'N/A'}>
                                                                                Classification: <span className="font-medium text-base-content/80">{snippet.classification || 'N/A'}</span>
                                                                            </span>
                                                                        </div>
                                                                        <div className="flex justify-between items-center gap-2 pt-0.5">
                                                                            <span className="truncate" title={snippet.video?.source?.name || 'Unknown Source'}>
                                                                                Source: {snippet.video?.source?.name || 'Unknown Source'}
                                                                            </span>
                                                                            <span>
                                                                                Aired: {snippet.video_upload_date ? new Date(snippet.video_upload_date).toLocaleDateString() : (snippet.video?.date ? new Date(snippet.video.date).toLocaleDateString() : 'Unknown Date')}
                                                                            </span>
                                                                        </div>
                                                                        <div className="mt-2 text-right">
                                                                            <button
                                                                                onClick={(e) => { e.stopPropagation(); onPlaySnippet(snippet); }}
                                                                                className="btn btn-xs btn-ghost text-primary hover:bg-primary/10 p-1"
                                                                                title="Play Snippet"
                                                                            >
                                                                                <PlayCircleIcon className="w-5 h-5 mr-1" /> Play
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    ) : (
                                                        <p className="text-xs italic text-base-content/70">
                                                            {selectedClassifications && selectedClassifications.size > 0 ? `No high value snippets found for the selected classifications.` : "No high value snippets found for this period."}
                                                        </p>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                </tbody>
            </table>
            {isLoadingSnippetsAndStats && displayedPlayers.length > 0 && !isLoadingPlayers && (
                <div className="text-center pt-2"><LoadingSpinner size="sm"/> <span className="text-xs italic ml-1">Updating player stats...</span></div>
            )}
        </div>
    );
};

export default DesktopPlayerTable; 